import { Input, Select, Textarea } from '@artosera/ui';
import {
  DEVISES,
  PAS_ARRONDI_CENTIMES,
  POURCENTAGE_LOT_MAX,
  POURCENTAGE_LOT_MIN,
  SENS_ARRONDI,
  STATUTS_CIBLES_LOT,
  type ActionLotOeuvres,
} from '@artosera/shared';
import { useId, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpositions } from '../../../api/expositions.js';
import { useLieux } from '../../../api/lieux.js';
import { useSelections } from '../../../api/presentation.js';
import { Field } from '../../Field.js';
import { SANS_LIEU, saisiePrixInvalide, type ParametresLot } from './parametresLot.js';
import styles from './LotOeuvresDialog.module.css';

interface OptionChoix<T extends string> {
  valeur: T;
  libelle: string;
}

/** Un choix exclusif en pastilles de 44 px : lisible d'un coup d'œil, au pouce. */
function Choix<T extends string>({
  legende,
  valeur,
  options,
  onChange,
}: {
  legende: string;
  valeur: T | '';
  options: readonly OptionChoix<T>[];
  onChange: (valeur: T) => void;
}): JSX.Element {
  const nom = useId();
  return (
    <fieldset className={styles.choix}>
      <legend className={styles.legende}>{legende}</legend>
      <div className={styles.pastilles}>
        {options.map((option) => (
          <label key={option.valeur} className={styles.pastille}>
            <input
              type="radio"
              name={nom}
              value={option.valeur}
              checked={valeur === option.valeur}
              onChange={() => onChange(option.valeur)}
            />
            <span>{option.libelle}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Le formulaire de l'étape « Réglage » : un seul composant pour les sept actions. */
export function FormulaireLot({
  type,
  galerieSlug,
  langue,
  parametres,
  onChange,
}: {
  type: ActionLotOeuvres;
  galerieSlug: string;
  langue: 'fr' | 'en';
  parametres: ParametresLot;
  onChange: (suite: Partial<ParametresLot>) => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const id = useId();
  const lieux = useLieux(galerieSlug, true);
  const expositions = useExpositions(galerieSlug, {}, { enabled: type === 'exposition' });
  const selections = useSelections(
    galerieSlug,
    { archivee: false, parPage: 100 },
    type === 'selection',
  );
  const tr = (cle: string, valeurs?: Record<string, unknown>): string =>
    t(`regie:oeuvres.lot.${cle}`, valeurs);

  const raison = (
    <Field label={tr('champs.raison')} htmlFor={`${id}-raison`}>
      <Textarea
        id={`${id}-raison`}
        rows={2}
        maxLength={500}
        value={parametres.raison}
        onChange={(event) => onChange({ raison: event.target.value })}
      />
    </Field>
  );

  let contenu: ReactNode = null;
  switch (type) {
    case 'statut':
      contenu = (
        <>
          <Choix
            legende={tr('champs.statut')}
            valeur={parametres.statut}
            options={STATUTS_CIBLES_LOT.map((code) => ({
              valeur: code,
              libelle: t(`oeuvres:statuts.${code}`),
            }))}
            onChange={(statut) => onChange({ statut })}
          />
          {raison}
        </>
      );
      break;
    case 'localisation':
      contenu = (
        <Field label={tr('champs.lieu')} htmlFor={`${id}-lieu`}>
          <Select
            id={`${id}-lieu`}
            value={parametres.lieu}
            onValueChange={(lieu) => onChange({ lieu })}
            placeholder={tr('champs.lieuPlaceholder')}
            options={[
              { value: SANS_LIEU, label: tr('champs.sansLieu') },
              ...(lieux.data ?? [])
                .filter((lieu) => lieu.actif)
                .map((lieu) => ({ value: lieu.id, label: lieu.nom })),
            ]}
          />
        </Field>
      );
      break;
    case 'publication':
      contenu = (
        <Choix
          legende={tr('champs.publication')}
          valeur={parametres.publication}
          options={[
            { valeur: 'publier', libelle: tr('champs.publier') },
            { valeur: 'retirer', libelle: tr('champs.retirer') },
          ]}
          onChange={(publication) => onChange({ publication })}
        />
      );
      break;
    case 'exposition': {
      const ouvertes = (expositions.data?.expositions ?? []).filter((e) => !e.archive);
      contenu = (
        <Field label={tr('champs.exposition')} htmlFor={`${id}-expo`}>
          <Select
            id={`${id}-expo`}
            value={parametres.expositionId}
            onValueChange={(expositionId) => onChange({ expositionId })}
            placeholder={tr('champs.expositionPlaceholder')}
            options={ouvertes.map((e) => ({
              value: e.id,
              label: e.titre[langue] ?? e.titre.fr,
            }))}
          />
          {expositions.isSuccess && ouvertes.length === 0 ? (
            <p className={styles.aide}>{tr('champs.aucuneExposition')}</p>
          ) : null}
        </Field>
      );
      break;
    }
    case 'selection': {
      const ouvertes = (selections.data?.selections ?? []).filter((s) => !s.archivee);
      contenu = (
        <Field label={tr('champs.selection')} htmlFor={`${id}-selection`}>
          <Select
            id={`${id}-selection`}
            value={parametres.selectionId}
            onValueChange={(selectionId) => onChange({ selectionId })}
            placeholder={tr('champs.selectionPlaceholder')}
            options={ouvertes.map((s) => ({ value: s.id, label: s.titre }))}
          />
          {selections.isSuccess && ouvertes.length === 0 ? (
            <p className={styles.aide}>{tr('champs.aucuneSelection')}</p>
          ) : null}
        </Field>
      );
      break;
    }
    case 'archivage':
      contenu = (
        <>
          <p className={styles.aide}>{tr('champs.archivageAide')}</p>
          {raison}
        </>
      );
      break;
    case 'prix': {
      const pourcentage = parametres.prixMode === 'pourcentage';
      contenu = (
        <>
          <Choix
            legende={tr('champs.prixRegle')}
            valeur={parametres.prixMode}
            options={[
              { valeur: 'pourcentage', libelle: tr('champs.prixPourcentage') },
              { valeur: 'montant', libelle: tr('champs.prixMontant') },
            ]}
            onChange={(prixMode) => onChange({ prixMode, prixValeur: '' })}
          />
          <Choix
            legende={tr('champs.prixSens')}
            valeur={parametres.prixSens}
            options={[
              { valeur: 'hausse', libelle: tr('champs.hausse') },
              { valeur: 'baisse', libelle: tr('champs.baisse') },
            ]}
            onChange={(prixSens) => onChange({ prixSens })}
          />
          <div className={styles.ligneChamps}>
            <Field
              label={pourcentage ? tr('champs.pourcentage') : tr('champs.montant')}
              htmlFor={`${id}-valeur`}
              error={
                saisiePrixInvalide(parametres)
                  ? pourcentage
                    ? tr('champs.pourcentageInvalide', {
                        min: Math.abs(POURCENTAGE_LOT_MIN),
                        max: POURCENTAGE_LOT_MAX,
                      })
                    : tr('champs.montantInvalide')
                  : undefined
              }
            >
              <Input
                id={`${id}-valeur`}
                mono
                inputMode="decimal"
                autoComplete="off"
                value={parametres.prixValeur}
                onChange={(event) => onChange({ prixValeur: event.target.value })}
              />
            </Field>
            {pourcentage ? null : (
              <Field label={tr('champs.devise')} htmlFor={`${id}-devise`}>
                <Select
                  id={`${id}-devise`}
                  value={parametres.devise}
                  onValueChange={(devise) =>
                    onChange({ devise: DEVISES.find((code) => code === devise) ?? 'EUR' })
                  }
                  options={DEVISES.map((code) => ({ value: code, label: code }))}
                />
              </Field>
            )}
          </div>
          <div className={styles.ligneChamps}>
            <Field label={tr('champs.pas')} htmlFor={`${id}-pas`}>
              <Select
                id={`${id}-pas`}
                value={parametres.pas}
                onValueChange={(pas) => onChange({ pas })}
                placeholder={tr('champs.pasPlaceholder')}
                options={PAS_ARRONDI_CENTIMES.map((pas) => ({
                  value: String(pas),
                  label: tr(`pas.${String(pas)}`),
                }))}
              />
            </Field>
            <Field label={tr('champs.sensArrondi')} htmlFor={`${id}-sens`}>
              <Select
                id={`${id}-sens`}
                value={parametres.sensArrondi}
                onValueChange={(valeur) =>
                  onChange({ sensArrondi: SENS_ARRONDI.find((sens) => sens === valeur) ?? '' })
                }
                placeholder={tr('champs.sensPlaceholder')}
                options={SENS_ARRONDI.map((sens) => ({
                  value: sens,
                  label: tr(`sensArrondi.${sens}`),
                }))}
              />
            </Field>
          </div>
          <p className={styles.aide}>{tr('champs.arrondiAide')}</p>
        </>
      );
      break;
    }
  }

  return <div className={styles.formulaire}>{contenu}</div>;
}
